import React, { FC, useEffect, useRef } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { Brand } from '@clearcover/atlas';
import { AnimatedScreen } from 'Shared/components';
import { reportEvent } from 'Shared/analytics';
import { DISPLAY_DEFAULTS_BY_CODE } from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { ScratchResultModal } from './components/ScratchResultModal';
import { useScratchOff } from './useScratchOff';
import styles from './styles.module.css';

// ── Symbol SVG renderer ────────────────────────────────────────────────────────
// Close approximations of the Figma icons pending final asset export.

function renderSymbol(key: string): React.ReactNode {
  switch (key) {
    case 'ticket':
      return (
        <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
          <rect x="3" y="8" width="26" height="16" rx="3" fill="#d75d63" />
          <circle cx="10" cy="16" r="2.5" fill="rgba(255,255,255,0.6)" />
          <line
            x1="14"
            y1="11"
            x2="14"
            y2="21"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="1"
            strokeDasharray="2,2"
          />
          <rect
            x="15"
            y="12"
            width="10"
            height="2"
            rx="1"
            fill="rgba(255,255,255,0.7)"
          />
          <rect
            x="15"
            y="15.5"
            width="7"
            height="1.5"
            rx="0.75"
            fill="rgba(255,255,255,0.5)"
          />
          <rect
            x="15"
            y="18.5"
            width="8.5"
            height="1.5"
            rx="0.75"
            fill="rgba(255,255,255,0.5)"
          />
        </svg>
      );
    case 'diamond':
      return (
        <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
          <polygon
            points="16,4 28,13 16,28 4,13"
            fill="#9eebfd"
            stroke="rgba(0,0,0,0.1)"
            strokeWidth="0.5"
          />
          <polygon
            points="16,4 28,13 22,13 10,13 4,13"
            fill="rgba(255,255,255,0.3)"
          />
        </svg>
      );
    case 'star':
      return (
        <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
          <polygon
            points="16,3 19.5,12.5 29.5,12.5 21.5,18.5 24.5,28 16,22.5 7.5,28 10.5,18.5 2.5,12.5 12.5,12.5"
            fill="#f6c04b"
          />
        </svg>
      );
    case 'heart':
      return (
        <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
          <path
            d="M16 27 C16 27 4 19 4 11.5 C4 7.9 6.9 5 10.5 5 C12.7 5 14.7 6.1 16 7.8 C17.3 6.1 19.3 5 21.5 5 C25.1 5 28 7.9 28 11.5 C28 19 16 27 16 27Z"
            fill="#f6c04b"
          />
        </svg>
      );
    case 'club':
      return (
        <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
          <circle cx="16" cy="11" r="5" fill="#1a1a1a" />
          <circle cx="10" cy="17" r="5" fill="#1a1a1a" />
          <circle cx="22" cy="17" r="5" fill="#1a1a1a" />
          <rect x="14" y="18" width="4" height="7" rx="1" fill="#1a1a1a" />
          <rect x="11" y="23" width="10" height="3" rx="1.5" fill="#1a1a1a" />
        </svg>
      );
    default:
      return <span style={{ fontSize: '1.5rem' }}>★</span>;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ScratchOff: FC<RouteComponentProps> = () => {
  const history = useHistory();

  const returnToPortal = () => {
    reportEvent('26for26-scratch-off:return-to-portal');
    history.push('/rewards');
  };

  const {
    status,
    outcomes,
    scratchState,
    panels,
    revealedPanels,
    pendingResult,
    pendingOutcomeConfig,
    attemptError,
    hasRemainingEntries,
    remainingEntries,
    revealPanel,
    revealAll,
    dismissOutcome,
    startNewAttempt,
  } = useScratchOff();

  const hasReportedDisplay = useRef(false);
  useEffect(() => {
    if (
      !hasReportedDisplay.current &&
      (scratchState === 'idle' || scratchState === 'no_entries')
    ) {
      hasReportedDisplay.current = true;
      reportEvent('26for26-scratch-off:display');
    }
  }, [scratchState]);

  const isLoading = scratchState === 'loading' || scratchState === 'posting';
  const isError = scratchState === 'error';

  const tierName =
    pendingOutcomeConfig != null
      ? (DISPLAY_DEFAULTS_BY_CODE[pendingOutcomeConfig.code]?.legendLabel ??
        undefined)
      : undefined;

  const outcomeDescription =
    pendingOutcomeConfig?.isWin && pendingResult?.prizeAmount != null
      ? `${tierName ?? ''} prize! $${pendingResult.prizeAmount.toLocaleString('en-US')} cash is on its way to your Awardco account within 5 business days.`
      : undefined;

  const legendItems = outcomes
    .filter((o) => o.isWin)
    .sort((a, b) => (b.prizeAmount ?? 0) - (a.prizeAmount ?? 0))
    .map((o) => ({
      dot: DISPLAY_DEFAULTS_BY_CODE[o.code]?.legendDotColor ?? o.display.color,
      label: `$${(o.prizeAmount ?? 0).toLocaleString('en-US')} ${DISPLAY_DEFAULTS_BY_CODE[o.code]?.legendLabel ?? o.code} ${o.weight}%`,
    }));

  return (
    <div className={styles.page}>
      <div className={styles.chrome}>
        <Brand
          variant="wordmark"
          color="white"
          height={24}
          className={styles.logo}
        />
        {status && (
          <div className={styles.entriesBadge} data-testid="entries-badge">
            <span className={styles.entriesCount}>
              {status.availableEntries}
            </span>{' '}
            <span className={styles.entriesLabel}>
              {status.availableEntries === 1 ? 'entry' : 'entries'}
            </span>
          </div>
        )}
      </div>

      <AnimatedScreen
        loading={isLoading}
        error={
          isError
            ? 'Unable to load the scratch-off card. Please try again.'
            : null
        }
        testId="scratch-off"
      >
        <div className={styles.layout}>
          <header className={styles.header}>
            <p className={styles.eyebrow}>26 FOR &#39;26 · ACT 3</p>
            <h1 className={styles.title}>
              Scratch &amp; <span className={styles.titleAccent}>Win</span>
            </h1>
            <p className={styles.subtitle}>
              Reveal 3 matching symbols on a card to win the reward.
            </p>
          </header>

          {scratchState === 'no_entries' ? (
            <p
              className={styles.noEntriesMessage}
              data-testid="no-entries-message"
            >
              You have no scratch-off cards available. Bind more policies to
              earn entries.
            </p>
          ) : (
            <>
              <div className={styles.cardPanel}>
                <div className={styles.cardPanelHeader}>
                  <span className={styles.cardPanelLabel}>
                    SCRATCH CARD · MATCH 3
                  </span>
                  <span className={styles.cardCountPill}>
                    {status?.availableEntries ?? 0} cards
                  </span>
                </div>

                <div className={styles.grid} data-testid="scratch-grid">
                  {panels.map((symbol, i) => (
                    <button
                      key={i}
                      className={
                        revealedPanels[i]
                          ? styles.panelRevealed
                          : styles.panelUnscratched
                      }
                      onClick={() => {
                        reportEvent('26for26-scratch-off:panel:click');
                        revealPanel(i);
                      }}
                      disabled={revealedPanels[i] || scratchState !== 'idle'}
                      data-testid={`panel-${i}`}
                      aria-label={
                        revealedPanels[i]
                          ? `Panel ${i + 1}: ${symbol}`
                          : `Scratch panel ${i + 1}`
                      }
                    >
                      {revealedPanels[i] && (
                        <span
                          className={styles.symbol}
                          aria-hidden="true"
                          data-testid={`symbol-${i}`}
                        >
                          {renderSymbol(symbol)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.actions}>
                <button
                  className={styles.revealAllButton}
                  onClick={() => {
                    reportEvent('26for26-scratch-off:reveal-all:click');
                    revealAll();
                  }}
                  disabled={scratchState !== 'idle'}
                  data-testid="reveal-all-button"
                >
                  Reveal All
                </button>

                {/* POST /attempts is idempotent until accept — New Card on an unscratched card replays the same predetermined outcome (anti-abuse); a new outcome comes after the current card is played. */}
                <button
                  className={styles.newCardButton}
                  onClick={() => {
                    if (
                      status &&
                      status.availableEntries >= 1 &&
                      !revealedPanels.some(Boolean)
                    ) {
                      reportEvent('26for26-scratch-off:new-card:click');
                      startNewAttempt();
                    }
                  }}
                  disabled={
                    scratchState !== 'idle' ||
                    revealedPanels.some(Boolean) ||
                    !status ||
                    status.availableEntries < 1
                  }
                  data-testid="new-card-button"
                >
                  NEW CARD
                </button>

                {attemptError && (
                  <p
                    className={styles.attemptError}
                    data-testid="attempt-error"
                  >
                    Something went wrong. Please try again.
                  </p>
                )}
              </div>

              <p className={styles.remainingText}>
                You have {status?.availableEntries ?? 0} cards remaining
              </p>
            </>
          )}

          {legendItems.length > 0 && (
            <div className={styles.legendRow}>
              {legendItems.map(({ dot, label }) => (
                <span key={label} className={styles.legendPill}>
                  <span
                    className={styles.legendDot}
                    style={{ background: dot }}
                    aria-hidden="true"
                  />
                  {label}
                </span>
              ))}
            </div>
          )}

          <button
            className={styles.returnLink}
            onClick={returnToPortal}
            data-testid="return-to-portal"
          >
            Return to Portal
          </button>
        </div>

        {scratchState === 'revealed' && pendingResult && (
          <ScratchResultModal
            isWin={pendingOutcomeConfig?.isWin ?? false}
            prizeAmount={pendingResult.prizeAmount}
            tierName={tierName}
            outcomeDescription={outcomeDescription}
            remainingCards={remainingEntries}
            hasRemainingCards={hasRemainingEntries}
            onScratchAnother={() => {
              reportEvent('26for26-scratch-off:outcome-modal:scratch-again');
              startNewAttempt();
            }}
            onClose={() => {
              reportEvent('26for26-scratch-off:outcome-modal:close');
              dismissOutcome();
              returnToPortal();
            }}
          />
        )}
      </AnimatedScreen>
    </div>
  );
};
